import React, {
  useContext,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { CiterightContext } from './CiterightContextProvider';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { TextButton } from 'js/design_system/TextButton';
import Divider from 'js/design_system/Divider';
import { Banner } from 'js/design_system/Banner';
import { Icon } from 'js/design_system/Icon';
import styled from 'astroturf/react';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import ClaimFilterPanel, { ClaimFilter } from './components/ClaimFilterPanel';
import ClaimCard from './components/ClaimCard';
import WorkCard from './components/WorkCard';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const FloatingNavButtons = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  z-index: 1000;
`;

const NavButton = styled.button`
  background: var(--surface-primary, #ffffff);
  border: 1px solid var(--border-primary, #e2e8f0);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary, #1a202c);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--surface-secondary, #f7fafc);
    border-color: var(--border-secondary, #cbd5e0);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const TopActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-xs-12);
`;

export const TriagePage = ({
  onClaimSelect,
  claimFilter = 'all',
  onClaimFilterChange,
  showBackToOverview = false,
  onBackToOverview,
}: {
  onClaimSelect: (claimId: string) => void;
  claimFilter?: ClaimFilter;
  onClaimFilterChange?: (filter: ClaimFilter) => void;
  showBackToOverview?: boolean;
  onBackToOverview?: () => void;
}) => {
  const context = useContext(CiterightContext);

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [highlightedReferenceId, setHighlightedReferenceId] = useState<
    string | null
  >(null);
  const referenceRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const workRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Refs for section navigation
  const claimsSectionRef = useRef<HTMLDivElement>(null);
  const referencesSectionRef = useRef<HTMLDivElement>(null);
  const authorsSectionRef = useRef<HTMLDivElement>(null);

  const reportClaims = context?.report?.claims;
  const reportReferences = context?.report?.references;
  const claims = useMemo(() => reportClaims || [], [reportClaims]);
  const references = useMemo(() => reportReferences || [], [reportReferences]);

  const scrollToReference = useCallback((referenceId: string) => {
    const element = referenceRefs.current.get(referenceId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedReferenceId(referenceId);
      setTimeout(() => {
        setHighlightedReferenceId(null);
      }, 2000);
    }
  }, []);

  const scrollToSection = useCallback(
    (sectionRef: React.RefObject<HTMLDivElement>) => {
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [],
  );

  const registerReferenceRef = useCallback(
    (referenceId: string) => (element: HTMLDivElement | null) => {
      if (element) {
        referenceRefs.current.set(referenceId, element);
      } else {
        referenceRefs.current.delete(referenceId);
      }
    },
    [],
  );

  const registerWorkRef = useCallback(
    (workId: string) => (element: HTMLDivElement | null) => {
      if (element) {
        workRefs.current.set(workId, element);
      } else {
        workRefs.current.delete(workId);
      }
    },
    [],
  );

  const processedClaims = useMemo(() => {
    return claims
      .filter((c) => !dismissedIds.has(c.id))
      .filter((claim) => {
        if (claimFilter === 'all') return true;
        if (claimFilter === 'essential') return claim.priority === 'High';
        if (claimFilter === 'optional') return claim.priority === 'Low';
        return true;
      })
      .sort((a, b) => {
        // Sort by chunk_index first, then by claim_index
        const chunkDiff = (a.chunk_index || 0) - (b.chunk_index || 0);
        if (chunkDiff !== 0) return chunkDiff;
        return (a.claim_index || 0) - (b.claim_index || 0);
      });
  }, [claims, dismissedIds, claimFilter]);

  const visibleClaims = processedClaims;

  // Check if any reference suggestions are displayed
  const hasAnyReferences = visibleClaims.some(
    (claim) =>
      claim.search?.done && (claim.search?.suggested_works?.length || 0) > 0,
  );

  return (
    <Container>
      <FloatingNavButtons>
        {claims.length > 0 && (
          <NavButton onClick={() => scrollToSection(claimsSectionRef)}>
            Claims
          </NavButton>
        )}
        {references.length > 0 && (
          <NavButton onClick={() => scrollToSection(referencesSectionRef)}>
            References
          </NavButton>
        )}
        {(context?.report?.author_report?.length || 0) > 0 && (
          <NavButton onClick={() => scrollToSection(authorsSectionRef)}>
            Suggested Authors
          </NavButton>
        )}
      </FloatingNavButtons>

      {context?.report && (
        <TopActionsBar>
          {showBackToOverview && onBackToOverview ? (
            <TextButton onClick={onBackToOverview} icon="arrow_back">
              Back to Overview
            </TextButton>
          ) : (
            <div />
          )}
          <ClaimFilterPanel
            onFilterChange={onClaimFilterChange}
            currentFilter={claimFilter}
            disabled={claims.length === 0}
            essentialCount={claims.filter((c) => c.priority === 'High').length}
            optionalCount={claims.filter((c) => c.priority === 'Low').length}
          />
        </TopActionsBar>
      )}

      {(context?.error || context?.report?.error) && (
        <Banner
          type="danger"
          border="all"
          icon={<Icon name="error_outline" size={16} />}
          actions={[
            {
              content: 'Report error and reload',
              onClick: () => window.location.reload(),
            },
          ]}
        >
          {context?.error || context?.report?.error}
        </Banner>
      )}

      {visibleClaims.length === 0 && context?.report?.done ? (
        <Body style={{ color: 'var(--text-tertiary, #718096)' }}>
          {(() => {
            if (claims.length === 0) {
              return 'No claims yet. Select text in the editor and add to triage, or import a document.';
            }
            if (claimFilter === 'essential') {
              return 'No essential claims found. Try selecting "All Claims" to see optional claims.';
            }
            if (claimFilter === 'optional') {
              return 'No optional claims found. Try selecting "All Claims" to see essential claims.';
            }
            return 'No claims match the current filter. Try selecting "All Claims".';
          })()}
        </Body>
      ) : (
        <div
          ref={claimsSectionRef}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {visibleClaims.length > 0 && (
            <ImpressionWatcher
              source="citeright.claims_displayed"
              extra={{ claim_count: visibleClaims.length }}
            >
              <div style={{ display: 'contents' }}>
                {hasAnyReferences && (
                  <ImpressionWatcher source="citeright.reference_suggestions_displayed">
                    <div style={{ display: 'contents' }}>
                      {visibleClaims.map((claim, index) => (
                        <ClaimCard
                          key={claim.id}
                          claim={claim}
                          index={index}
                          references={references}
                          onClaimSelect={onClaimSelect}
                          onDismiss={(claimId) =>
                            setDismissedIds((prev) =>
                              new Set(prev).add(claimId),
                            )
                          }
                          onReferenceScroll={scrollToReference}
                          pageContext="inline_report"
                        />
                      ))}
                    </div>
                  </ImpressionWatcher>
                )}
                {!hasAnyReferences && (
                  <div style={{ display: 'contents' }}>
                    {visibleClaims.map((claim, index) => (
                      <ClaimCard
                        key={claim.id}
                        claim={claim}
                        index={index}
                        references={references}
                        onClaimSelect={onClaimSelect}
                        onDismiss={(claimId) =>
                          setDismissedIds((prev) => new Set(prev).add(claimId))
                        }
                        onReferenceScroll={scrollToReference}
                        pageContext="inline_report"
                      />
                    ))}
                  </div>
                )}
              </div>
            </ImpressionWatcher>
          )}
        </div>
      )}

      {references.length > 0 && (
        <>
          <Divider.s24 />
          <div ref={referencesSectionRef}>
            <Heading size="sm" as="h3">
              References
            </Heading>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {references.map((reference) => {
                const refId =
                  reference.reference_identifier || reference.work.id;
                const workId = reference.work.id;
                return (
                  <WorkCard
                    key={refId}
                    ref={(element: HTMLDivElement | null) => {
                      registerReferenceRef(refId)(element);
                      registerWorkRef(workId)(element);
                    }}
                    workData={reference}
                    isHighlighted={highlightedReferenceId === refId}
                    pageContext="inline_report"
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
      {(context?.report?.author_report?.length || 0) > 0 && (
        <>
          <Divider.s24 />
          <div ref={authorsSectionRef}>
            <Heading size="sm" as="h3">
              Suggested Authors
            </Heading>
            <Body
              size="sm"
              style={{
                marginTop: 8,
                marginBottom: 16,
                color: 'var(--text-secondary)',
              }}
            >
              These authors appear frequently in the references we've identified
              for you
            </Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {context?.report?.author_report?.map((suggestedAuthor) => (
                <div
                  key={suggestedAuthor.author_name.surname}
                  style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                  <Heading size="xs" as="h4">
                    {suggestedAuthor.author_name.first_initial
                      ? `${suggestedAuthor.author_name.surname}, ${suggestedAuthor.author_name.first_initial}.`
                      : suggestedAuthor.author_name.surname}
                    <span style={{ fontWeight: 'normal' }}>
                      {' '}
                      — author of {suggestedAuthor.suggested_works.length}{' '}
                      similar{' '}
                      {suggestedAuthor.suggested_works.length === 1
                        ? 'work'
                        : 'works'}
                    </span>
                  </Heading>
                  {suggestedAuthor.referenced_works.length > 0 && (
                    <>
                      <Heading size="xs" as="h5">
                        Cited Works
                      </Heading>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                        }}
                      >
                        {suggestedAuthor.referenced_works.map((work, index) => (
                          <WorkCard
                            key={index}
                            workData={work}
                            pageContext="inline_report"
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {suggestedAuthor.suggested_works.length > 0 && (
                    <>
                      <Heading size="xs" as="h5">
                        Alternative Citations
                      </Heading>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                        }}
                      >
                        {suggestedAuthor.suggested_works
                          .slice(0, 3)
                          .map((work, index) => (
                            <WorkCard
                              key={index}
                              workData={work}
                              pageContext="inline_report"
                            />
                          ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Container>
  );
};
